import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "pb-4"
}
const _hoisted_2 = {
  class: "grid grid-cols-2 gap-x-4",
  tabindex: "-1"
}

import { ref, computed, watch } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { VisuallyHidden } from 'radix-vue';
import { useToast } from 'vue-toastification';
import { addWeeks, format, parseISO } from 'date-fns';
import CustomButton from '@/components/ui/CustomButton.vue';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { queryKeys } from '@/plugins/store/actions/queries/study-periods';
import DatePicker from '@/components/ui/DatePicker.vue';
import CustomInput from '@/components/ui/CustomInput.vue';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import ApiClient from '@/plugins/store/actions/api/ApiClient';
import { Barn, StudyPeriod } from '@/types';
import { i18n } from '@/plugins/internationalization/i18n';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

interface EditTeamState {
    locationNameAugmented: string;
    name: string;
    startDate: string;
    endDate: string;
    isFavorite: boolean;
    animalGroupId: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EditTeamModal',
  props: /*@__PURE__*/_mergeModels({
    state: {}
  }, {
    "modelValue": { type: Boolean },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["delete"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const queryClient = useQueryClient();
const toast = useToast();

const showModal = _useModel<boolean>(__props, 'modelValue');

const isLoading = ref(false);

function getInitialState() {
    return {
        locationNameAugmented: '',
        name: '',
        startDate: new Date().toISOString(),
        endDate: addWeeks(new Date(), 1).toISOString(),
        isFavorite: false,
        animalGroupId: '1',
    };
}

const animalGroupOptions = computed(() => [
    {
        value: 1,
        label: i18n.global.t('views.monitoring.teamOverview.modals.editTeam.fields.animalGroup.options.weaners'),
    },
    {
        value: 2,
        label: i18n.global.t('views.monitoring.teamOverview.modals.editTeam.fields.animalGroup.options.slaughterPigs'),
    },
    {
        value: 3,
        label: i18n.global.t('views.monitoring.teamOverview.modals.editTeam.fields.animalGroup.options.frats'),
    },
]);

const editState = ref<EditTeamState>(getInitialState());

async function handleEdit() {
    try {
        if (isLoading.value) {
            return;
        }

        isLoading.value = true;

        await ApiClient.send(
            'put',
            'monitor/study-periods/bulk-update',
            {
                skipHandleError: true,
            },
            {
                study_periods: [
                    {
                        id: props.state.team.id,
                        name: editState.value.name,
                        start: format(parseISO(editState.value.startDate), 'yyyy-MM-dd'),
                        end: editState.value.endDate ? format(parseISO(editState.value.endDate), 'yyyy-MM-dd') : null,
                        is_favorite: editState.value.isFavorite,
                        animal_group_id: Number(editState.value.animalGroupId),
                    },
                ],
            }
        );

        await queryClient.invalidateQueries({ queryKey: queryKeys.getStudyPeriodsRoot });

        showModal.value = false;

        toast.success(i18n.global.t('views.monitoring.teamOverview.modals.editTeam.successMessages.teamWasEdited'));
    } catch (err) {
        const errorMessage = (err as any)?.data?.message || i18n.global.t('views.monitoring.teamOverview.modals.editTeam.errors.couldNotEditTeam');
        toast.error(errorMessage);
    } finally {
        isLoading.value = false;
    }
}

const isFormValid = computed(() => editState.value.name.length > 0);

watch(showModal, (value) => {
    if (value) {
        editState.value = {
            locationNameAugmented: props.state.team.location_name_augmented ?? '',
            name: props.state.team.name ?? '',
            startDate: props.state.team.start ? new Date(props.state.team.start as string).toISOString() : new Date().toISOString(),
            endDate: props.state.team.end ? new Date(props.state.team.end as string).toISOString() : '',
            isFavorite: props.state.team.is_favorite ?? false,
            animalGroupId: props.state.barn.animal_group_id?.toString() ?? '1',
        };
    }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showModal).value = $event)),
    disabled: isLoading.value,
    class: "sm:max-w-[540px]"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(DialogHeader), null, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogTitle), null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamOverview.modals.editTeam.title')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(VisuallyHidden), null, {
            default: _withCtx(() => [
              _createVNode(_unref(DialogDescription), { class: "pt-4" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamOverview.modals.editTeam.description')), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createElementVNode("form", {
        class: "mt-4 space-y-2",
        onSubmit: _withModifiers(handleEdit, ["prevent"])
      }, [
        _createVNode(CustomInput, {
          value: (editState.value?.locationNameAugmented as any) ?? '',
          disabled: ""
        }, null, 8, ["value"]),
        _createVNode(CustomInput, {
          value: (editState.value.name as any),
          "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => (((editState.value.name as any)) = $event)),
          rules: [{ validate: (value) => value.length > 0, message: '' }],
          required: "",
          label: _ctx.$t('views.monitoring.teamOverview.modals.editTeam.fields.name')
        }, null, 8, ["value", "rules", "label"]),
        (_ctx.state.barn.animal_group_id === 3)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createVNode(_unref(RadioGroup), {
                modelValue: editState.value.animalGroupId,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((editState.value.animalGroupId) = $event)),
                class: "gap-x-5"
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(animalGroupOptions.value, (option) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: option.value,
                      class: "flex items-center cursor-pointer"
                    }, [
                      _createVNode(_unref(RadioGroupItem), {
                        id: `option-id-${option.value}`,
                        value: option.value.toString()
                      }, null, 8, ["id", "value"]),
                      _createVNode(_unref(Label), {
                        for: `option-id-${option.value}`,
                        class: "font-normal cursor-pointer pl-2"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(option.label), 1)
                        ]),
                        _: 2
                      }, 1032, ["for"])
                    ]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(DatePicker, {
            value: editState.value.startDate,
            "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((editState.value.startDate) = $event)),
            required: "",
            label: _ctx.$t('views.monitoring.teamOverview.modals.editTeam.fields.startDate')
          }, null, 8, ["value", "label"]),
          _createVNode(DatePicker, {
            value: editState.value.endDate,
            "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((editState.value.endDate) = $event)),
            resetable: "",
            label: _ctx.$t('views.monitoring.teamOverview.modals.editTeam.fields.endDate')
          }, null, 8, ["value", "label"])
        ]),
        _createVNode(CustomCheckbox, {
          checked: editState.value.isFavorite,
          "onUpdate:checked": _cache[4] || (_cache[4] = ($event: any) => ((editState.value.isFavorite) = $event)),
          label: _ctx.$t('views.monitoring.teamOverview.modals.editTeam.fields.markAsFavorite'),
          class: "-ml-1"
        }, null, 8, ["checked", "label"])
      ], 32),
      _createVNode(CustomButton, {
        disabled: isLoading.value,
        "color-preset": "warning",
        onClick: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (emit('delete', props.state.team.id)), ["prevent"]))
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamOverview.modals.editTeam.buttons.deleteTeam')), 1)
        ]),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(_unref(DialogFooter), null, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogClose), { "as-child": "" }, {
            default: _withCtx(() => [
              _createVNode(CustomButton, {
                disabled: isLoading.value,
                "color-preset": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamOverview.modals.editTeam.buttons.cancel')), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            _: 1
          }),
          _createVNode(CustomButton, {
            loading: isLoading.value,
            disabled: !isFormValid.value,
            onClick: handleEdit
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamOverview.modals.editTeam.buttons.save')), 1)
            ]),
            _: 1
          }, 8, ["loading", "disabled"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue", "disabled"]))
}
}

})